import type { BrandImportField, CommercePlatform, CommercePlatformSource, ImportFieldGap, ProductImportField } from '@repo/types'

import { api } from './index'

/**
 * VRG-311 — the AE-facing acquisition demo console. Talks to `DemoAdminController`
 * (`apps/api/src/infrastructure/controllers/demo.admin.controller.ts`), which is
 * already built and verified (VRG-305/307/308/309/310/313) — this file is a thin,
 * typed client over it. Do NOT change apps/api.
 */

export interface DemoListItem {
  companyId: string
  companyName: string
  /**
   * The brand mark scraped at provisioning. Null once purged — the storage
   * object is deleted with the company, so a link to it would 404.
   */
  logo: string | null
  subdomain: string
  prospectDomain: string | null
  prospectContact: string | null
  provisionedByEmail: string | null
  provisionedAt: string
  sendTier: string | null
  sentAt: string | null
  firstOpenedAt: string | null
  lastOpenedAt: string | null
  openCount: number
  purgedAt: string | null
  demoExpiresAt: string | null
  /**
   * VRG-361 — the invited admin's CONSOLE ACCESS trial, independent of
   * demoExpiresAt (the public certificate's lifetime). `trialStartedAt` is null
   * until an admin accepts the invite, unless an AE pre-set `trialEndsAt` before
   * that happened (ExtendTrialUseCase supports both).
   */
  trialStartedAt: string | null
  trialEndsAt: string | null
  /**
   * VRG-361 Slice 2 — the billing-takeover stamp. Non-null means this brand is a
   * paying customer now. The row stays on this list afterwards because the demo
   * history and the customer fact are both true at once (AC1).
   */
  demoTakenOverAt: string | null
  /**
   * Brand people who have actually SIGNED IN — NOT a raw `company_users` count.
   * Inviting writes the membership row immediately, so a plain count includes
   * unopened invitation emails. Takeover requires somebody to have actually
   * joined, so this is what gates the action in the UI.
   */
  joinedUserCount: number
  /**
   * Brand people invited AT ALL, signed in or not. The pair with
   * `joinedUserCount` is what makes the row legible: `0 / 0` nobody invited yet,
   * `1 / 0` an invitation is out and unanswered, `1 / 1` takeover is unblocked.
   * Without it the first two states are indistinguishable, and they are opposite
   * next actions for the AE.
   */
  invitedUserCount: number
  /** Days since provisioning. */
  ageDays: number
  /** Sent but never opened. */
  neverOpened: boolean
  /** The company row is gone; this is the surviving audit record. */
  isPurged: boolean
  /**
   * VRG-723 — the demo site's address, built by the API from CUSTOMER_APP_URL
   * (`<subdomain>.app.vera.inc` on prod, `.app.dev.vera.inc` on dev). Shown and copied as is: the
   * platform never builds a demo host of its own.
   */
  demoUrl: string
  /** VRG-723 — the GTM → Sales company the demo was made for, or null. */
  salesAccountId: string | null
  /** VRG-723 — the certificate the AE sends; null once purged. */
  outreachCertificate: { id: string; productTitle: string; url: string } | null
}

export interface DemoThemeSourceUrl {
  url: string
  fetchedAt: string
  purpose: string
}

/**
 * A proposal only (VRG-307) — nothing is written until `provisionDemo` is called
 * with an AE-approved copy of this. Every scrape failure degrades to a documented
 * neutral default rather than an error, so this shape is always complete even when
 * `failed` is set.
 */
export interface ProposedDemoTheme {
  logo: string | null
  colors: {
    primary: string
    secondary: string
    background: string
  }
  fonts: {
    heading: string
    body: string
  }
  confidence: number
  failed?: { reason: string }
  sourceUrls: DemoThemeSourceUrl[]
  /** VRG-723 — logo, colors, fonts that could not be pulled in (or were guessed), and why. */
  missing?: ImportFieldGap<BrandImportField>[]
}

export interface ProposedDemoProduct {
  title: string
  sku: string | null
  description: string | null
  images: string[]
  price: number | null
  compareAtPrice: number | null
  type: string | null
  source: { url: string; fetchedAt: string }
  /**
   * VRG-716 — the product's own listing page on the Shopify paths, where `source.url` is the API
   * endpoint that was read (`/products.json`, `/products/<h>.json`). Absent on JSON-LD.
   */
  productUrl?: string
  /** VRG-723 — the fields that could not be pulled in for this product, and why. */
  missing?: ImportFieldGap<ProductImportField>[]
}

export type ProductSeedPath = 'shopify' | 'json-ld' | 'none'

/** `path: 'none'` is a first-class outcome, not an error — `reason` explains it. */
export interface ProposedProductSeedResult {
  path: ProductSeedPath
  products: ProposedDemoProduct[]
  reason?: string
}

/** VRG-716 — mirrors `CommercePlatformDetection` (apps/api demo/shared/detect-commerce-platform.ts). */
export interface CommercePlatformDetection {
  platform: CommercePlatform
  /** The evidence: `cdn.shopify.com`, `header x-shopid`, `/products.json`… */
  signals: string[]
}

export interface ProposeDemoResponse {
  theme: ProposedDemoTheme | null
  products: ProposedProductSeedResult | null
  /** VRG-716 — read from the pages the propose already fetched. */
  commercePlatform?: CommercePlatformDetection
  themeError: string | null
  productsError: string | null
}

export interface ProvisionDemoProductInput {
  title: string
  sku?: string
  images?: string[]
  description?: string
  sourceUrl?: string
}

export interface ProvisionDemoBody {
  brandName: string
  prospectDomain: string
  prospectContact?: string
  sendTier?: 'tier_1' | 'tier_2' | 'tier_3' | 'tier_4' | 'tier_5'
  theme?: Record<string, unknown>
  products?: ProvisionDemoProductInput[]
  /** ISO date. Omit for "never expires" (the default — VRG-305 decision 9). */
  expiresAt?: string
  /** VRG-716 — carried to the sales company by the sales sync. */
  commercePlatform?: CommercePlatform
  /** `manual` when the AE changed the proposed value. */
  commercePlatformSource?: CommercePlatformSource
  /**
   * VRG-723 — the GTM → Sales company the demo is for: picked (`existing`), or created with the
   * demo (`new`). The API links it directly; `industry` names a new company's, or fills a blank one.
   */
  salesAccount?: { mode: 'existing'; id: string; industry?: string | null } | { mode: 'new'; industry?: string | null }
  /** VRG-723 — index in `products` of the outreach certificate's product (default 0). */
  outreachProductIndex?: number
}

export interface ProvisionDemoResponse {
  companyId: string
  subdomain: string
  /** The outreach certificate. */
  certificateId: string
  /** VRG-723 — the sales company it is linked to, and whether provisioning created it. */
  salesAccountId: string
  salesAccountCreated: boolean
  /** The outreach certificate on the demo site, on the customer app host (API-built). */
  demoUrl: string
}

/** VRG-723 — mirrors `OutreachCertificateView` (apps/api demo/outreach-certificate). */
export interface OutreachCertificateView {
  companyId: string
  companyName: string
  subdomain: string
  logo: string | null
  theme: { primaryColor: string | null; fontHeading: string | null; fontBody: string | null }
  certificate: {
    id: string
    productId: string
    productTitle: string
    sku: string | null
    description: string | null
    images: string[]
    url: string
  } | null
  products: Array<{ productId: string; title: string; image: string | null; certificateId: string | null }>
  canChange: boolean
}

export interface DemoPurgeManifest {
  companyId: string
  subdomain: string | null
  deletedRows: Record<string, number>
  storage: { deleted: string[]; failed: string[]; alreadyGone: string[] }
  cachesCleared: string[]
  cdnInvalidationRequired: boolean
  [key: string]: unknown
}

export interface ExtendDemoResponse {
  companyId: string
  subdomain: string | null
  demoExpiresAt: string | null
}

/** VRG-361 — the demo's console trial deadline, after an "Extend trial" call. */
export interface ExtendTrialResponse {
  companyId: string
  subdomain: string | null
  trialEndsAt: string | null
}

/**
 * VRG-369 — one product added to an existing demo, with the certificate minted
 * for it.
 *
 * `demoUrl` is per-CERTIFICATE and that matters: the invite link is pinned to the
 * certificate provisioning created, so a product added after the send is only
 * reachable through the URL returned here. Surfacing it is the difference between
 * adding a product and adding one the prospect can actually be shown.
 */
export interface AddedDemoProduct {
  productId: string
  title: string
  certificateId: string
  demoUrl: string
}

export interface AddDemoProductsResponse {
  companyId: string
  subdomain: string | null
  products: AddedDemoProduct[]
}

/**
 * VRG-361 Slice 2 — the post-takeover state of a company, echoed back so the UI
 * can report what actually happened rather than asserting it.
 *
 * `demoCertificatesRetained` is deliberately part of the contract: the demo
 * certificates are NOT deleted (AC4 as reworded — purging a tagged certificate
 * turns a physical card already in someone's hand into a dead scan), so the count
 * is how the AE learns they are still there and still badged.
 */
export interface TakeOverDemoResponse {
  companyId: string
  companyName: string
  kind: string
  takenOverAt: string
  isExample: boolean
  veraGrantRowsDeleted: number
  /** VRG-677: the new-brand default Vera-support grant (`ai_view`) was seeded in its place. */
  veraDefaultGrantSeeded: boolean
  joinedUserCount: number
  demoCertificatesRetained: number
  subdomain: string
  /** The `-demo`-stripped name the brand can now claim in Settings. */
  claimableSubdomain: string | null
  claimableSubdomainAvailable: boolean
}

/**
 * VRG-361 Slice 2 — the result of inviting a brand admin into a demo.
 *
 * `workosOrgCreated` is part of the contract rather than an implementation detail:
 * the org is created LAZILY at first invite (provisioning never creates one), so
 * this is the AE's only signal that the first invitation for this brand has just
 * brought an external organization into existence.
 */
export interface InviteDemoBrandUserResponse {
  companyId: string
  companyName: string
  subdomain: string | null
  workosOrgId: string
  workosOrgCreated: boolean
  /** Always `company_admin` — the API does not accept a role for this route. */
  roleName: string
  invitedEmails: string[]
  totalInvited: number
}

export const demosApi = {
  /** VRG-723 — the demo's outreach certificate: link, PNG data, and the products to change it to. */
  async getOutreachCertificate(companyId: string): Promise<OutreachCertificateView> {
    const response = await api.get(`/demos/admin/${companyId}/outreach-certificate`)
    if (response.status >= 400) {
      throw new Error((response.data as { message?: string })?.message ?? `Could not load the outreach certificate (${response.status}).`)
    }
    return response.data
  },

  /** VRG-723 — make this product's certificate the outreach certificate (one is minted if needed). */
  async setOutreachCertificate(companyId: string, productId: string): Promise<OutreachCertificateView> {
    const response = await api.patch(`/demos/admin/${companyId}/outreach-certificate`, { productId })
    if (response.status >= 400) {
      throw new Error((response.data as { message?: string })?.message ?? `Could not change the outreach certificate (${response.status}).`)
    }
    return response.data
  },

  /**
   * VRG-723 — the bytes of the demo's logo or a product photo, fetched by the API, so the PNG canvas
   * is never tainted by a cross-origin image (which would make `toBlob` throw).
   */
  async getOutreachImage(companyId: string, src: string): Promise<Blob> {
    const response = await api.get(`/demos/admin/${companyId}/outreach-certificate/image`, {
      params: { src },
      responseType: 'blob',
    })
    if (response.status >= 400 || !(response.data instanceof Blob)) {
      throw new Error(`Could not load that image (${response.status}).`)
    }
    return response.data
  },

  async listDemos(includePurged: boolean): Promise<DemoListItem[]> {
    const response = await api.get('/demos/admin', {
      params: { includePurged: includePurged ? 'true' : 'false' },
    })
    return response.data
  },

  async proposeDemo(url: string): Promise<ProposeDemoResponse> {
    const response = await api.post('/demos/admin/propose', { url })
    return response.data
  },

  /** VRG-716 — exactly the product a pasted listing URL names, never the store's catalogue. */
  async importDemoProduct(url: string): Promise<ProposedProductSeedResult> {
    const response = await api.post('/demos/admin/import-product', { url })
    return response.data
  },

  async provisionDemo(body: ProvisionDemoBody): Promise<ProvisionDemoResponse> {
    const response = await api.post('/demos/admin', body)
    return response.data
  },

  async purgeDemo(companyId: string, reason: string): Promise<DemoPurgeManifest> {
    const response = await api.delete(`/demos/admin/${companyId}`, {
      data: { reason },
    })
    return response.data
  },

  /**
   * VRG-369 — add products to a demo that has already been issued, without
   * entering its space.
   *
   * Same explicit status check as `extendDemo` below, for the same reason: the
   * shared client RESOLVES on an HTTP error, so without this a 403 from the
   * kind-guard would render as "products added" and the AE would go looking for
   * rows that were never written.
   */
  async addDemoProducts(
    companyId: string,
    products: ProvisionDemoProductInput[],
  ): Promise<AddDemoProductsResponse> {
    const response = await api.post(`/demos/admin/${companyId}/products`, { products })
    if (response.status >= 400) {
      throw new Error(
        (response.data as { message?: string })?.message ??
          `Could not add products to this demo (${response.status}).`,
      )
    }
    return response.data
  },

  /**
   * VRG-361 — change a demo's lifetime. `null` means never expires, and it is a
   * real choice rather than an absence, so the field is ALWAYS sent. The endpoint
   * rejects an absent field on purpose: a serialization slip must not be able to
   * quietly make a demo permanent.
   */
  async extendDemo(companyId: string, expiresAt: string | null): Promise<ExtendDemoResponse> {
    const response = await api.patch(`/demos/admin/${companyId}/expiry`, { expiresAt })
    // The shared client RESOLVES on an HTTP error — `createApiCall` ends in
    // `.catch(errorHandler)` and `errorHandler` RETURNS `error.response` rather
    // than re-throwing. So axios never rejects, react-query never calls onError,
    // and a mutation reports success on a 500. Caught live: this endpoint 500'd
    // and the UI showed "Demo lifetime updated".
    //
    // Checked here rather than fixed centrally because making the handler reject
    // changes behaviour at every call site in the platform, several of which read
    // `response.status` themselves and would start throwing. Tracked separately.
    if (response.status >= 400) {
      throw new Error(
        (response.data as {message?: string})?.message ??
          `Could not update the demo lifetime (${response.status}).`,
      )
    }
    return response.data
  },

  /**
   * VRG-361 — change the demo's CONSOLE TRIAL deadline: the invited admin's
   * console-access clock, never `demoExpiresAt` (the public certificate's
   * lifetime) — the two are deliberately independent. Unlike `extendDemo`, the
   * API requires an absolute, non-null date: an unbounded console trial would
   * mean "never bounce", which is a product decision this ticket never made.
   *
   * Same explicit status check as the siblings above, for the same reason: the
   * shared client resolves on an HTTP error rather than rejecting.
   */
  /**
   * VRG-361 Slice 2 — convert a demo into a real, billable customer account.
   *
   * Irreversible: `demo_taken_over_at` is stamped once and a second call is
   * refused, so the caller must gate this behind a confirmation that states the
   * consequences (working-conventions §43) rather than treating it as a toggle.
   *
   * The explicit status check matches every sibling write here. It is belt and
   * braces under the current client contract — `createApiCall` now rejects on
   * every failed request — but the refusals this endpoint returns (not a demo,
   * already taken over, nobody has signed in) each carry a message the AE needs
   * to READ, and this is what preserves it as the thrown error's text rather
   * than a generic status line.
   */
  /**
   * VRG-361 Slice 2 — invite a brand admin into a demo, which is the precondition
   * `takeOverDemo` below refuses without.
   *
   * No `roleId` is sent and the endpoint does not accept one: the role is fixed at
   * `company_admin` server-side. That is deliberate rather than an omission — the
   * shared invite path defaults an omitted role to `platform_manager` for internal
   * callers, so a client-supplied role is exactly the thing that must not exist here.
   *
   * Same explicit status check as every sibling write. The refusals carry messages
   * the AE needs to READ (not a demo, already taken over, a WorkOS send that failed
   * per-address), and this is what keeps them as the thrown error's text.
   */
  async inviteDemoBrandUser(
    companyId: string,
    emails: string[],
  ): Promise<InviteDemoBrandUserResponse> {
    const response = await api.post(`/demos/admin/${companyId}/invite`, { emails })
    if (response.status >= 400) {
      throw new Error(
        (response.data as { message?: string })?.message ??
          `Could not invite anyone into this demo (${response.status}).`,
      )
    }
    return response.data
  },

  async takeOverDemo(companyId: string): Promise<TakeOverDemoResponse> {
    const response = await api.post(`/demos/admin/${companyId}/takeover`, {})
    if (response.status >= 400) {
      throw new Error(
        (response.data as { message?: string })?.message ??
          `Could not convert this demo to a real account (${response.status}).`,
      )
    }
    return response.data
  },

  async extendTrial(companyId: string, trialEndsAt: string): Promise<ExtendTrialResponse> {
    const response = await api.patch(`/demos/admin/${companyId}/trial`, { trialEndsAt })
    if (response.status >= 400) {
      throw new Error(
        (response.data as { message?: string })?.message ??
          `Could not update the console trial deadline (${response.status}).`,
      )
    }
    return response.data
  },
}
